import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  fetchCkTradein,
  downloadCkTradeinCsv,
  listOpenCkTradeinBatches,
  createCkTradeinBatch,
  fetchCkTradeinBatch,
  fetchCkTradeinPickList,
  updateCkTradeinLine,
  removeCkTradeinLine,
  markCkTradeinLinePulled,
  undoCkTradeinLinePull,
  returnCkTradeinLineToStock,
  markCkTradeinBatchShipped,
  recordCkTradeinPayout,
  exportCkTradeinCsv,
  saveCkCsvPart,
  type CkTradeinCkCsvExport,
  type CkTradeinBatch,
  type CkTradeinBatchLine,
  type CkTradeinBatchSummary,
  type CkTradeinItem,
  type CkTradeinParams,
  type CkTradeinPickList,
  type CkTradeinResponse,
} from '../api/ckTradein';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../styles/tokens';
import { formatInstant, localCalendarDateIso, parseInstant } from '../utils/datetime';
import './ckTradeinPickList.css';

const WORKFLOW_STEPS = [
  { id: 'candidates', label: 'Candidates' },
  { id: 'pick-list', label: 'Pick list' },
  { id: 'mark-pulled', label: 'Mark pulled' },
  { id: 'export-ck-csv', label: 'Export CK CSV' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'record-payout', label: 'Record payout' },
] as const;

function candidateKey(item: CkTradeinItem): string {
  const printing = item.printing || (item.is_foil ? 'foil' : 'normal');
  return [item.mtgjson_uuid, item.condition || '', printing, item.language || ''].join('|');
}

function defaultActionQty(line: CkTradeinBatchLine): string {
  const remaining = line.qty_requested - line.qty_pulled;
  if (remaining > 0) {
    return String(remaining);
  }
  return String(line.qty_pulled);
}

function instantFromLocalDate(isoDate: string): string {
  const today = localCalendarDateIso();
  if (isoDate === today) {
    return new Date().toISOString();
  }
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) {
    return new Date().toISOString();
  }
  return new Date(year, month - 1, day, 12, 0, 0).toISOString();
}

function formatApiError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

export const CkTradeinPage: React.FC = () => {
  const { batchId } = useParams<{ batchId?: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<CkTradeinItem[]>([]);
  const [summary, setSummary] = useState<CkTradeinResponse['summary'] | null>(null);
  const [totalCards, setTotalCards] = useState(0);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [payout, setPayout] = useState<'cash' | 'credit'>('cash');
  const [topNInput, setTopNInput] = useState('500');
  const [minAdvantageInput, setMinAdvantageInput] = useState('0');
  const [minCkPriceInput, setMinCkPriceInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingCk, setIsExportingCk] = useState(false);
  const [ckExport, setCkExport] = useState<CkTradeinCkCsvExport | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [qtyByKey, setQtyByKey] = useState<Record<string, string>>({});
  const [openBatches, setOpenBatches] = useState<CkTradeinBatchSummary[]>([]);
  const [batch, setBatch] = useState<CkTradeinBatch | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [lineQty, setLineQty] = useState<Record<string, string>>({});
  const [pullQty, setPullQty] = useState<Record<string, string>>({});
  const [returnNote, setReturnNote] = useState<Record<string, string>>({});
  const [savingLineId, setSavingLineId] = useState<string | null>(null);
  const [workflowStep, setWorkflowStep] = useState<'batch' | 'pick-list'>('batch');
  const [pickList, setPickList] = useState<CkTradeinPickList | null>(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [shipDate, setShipDate] = useState(localCalendarDateIso);
  const [isShipping, setIsShipping] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [paidDate, setPaidDate] = useState(localCalendarDateIso);
  const [recordedMode, setRecordedMode] = useState<'cash' | 'credit'>('cash');
  const [isSavingPayout, setIsSavingPayout] = useState(false);
  const candidateErrorRef = useRef<HTMLDivElement>(null);
  const pickAllRef = useRef<HTMLInputElement>(null);
  // Bumped when Start batch or the open-batch load sets an error. loadReport
  // must not clear an error that landed while its request was in flight.
  const preservedErrorStamp = useRef(0);

  const buildParams = useCallback((): CkTradeinParams => {
    const params: CkTradeinParams = { payout };

    if (topNInput.trim()) {
      const parsed = parseInt(topNInput, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        params.top_n = parsed;
      }
    }

    if (minAdvantageInput.trim()) {
      const parsed = parseFloat(minAdvantageInput);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        params.min_advantage = parsed;
      }
    }

    if (minCkPriceInput.trim()) {
      const parsed = parseFloat(minCkPriceInput);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        params.min_ck_price = parsed;
      }
    }

    return params;
  }, [payout, topNInput, minAdvantageInput, minCkPriceInput]);

  const loadReport = useCallback(async () => {
    const stampAtStart = preservedErrorStamp.current;
    try {
      setIsLoading(true);
      const data = await fetchCkTradein(buildParams());
      setItems(data.items);
      setTotalCards(data.total_cards);
      setSummary(data.summary);
      setGeneratedAt(data.generated_at);
      setQtyByKey((prev) => {
        const next = { ...prev };
        for (const item of data.items) {
          const key = candidateKey(item);
          if (next[key] === undefined) {
            next[key] = String(item.copy_count);
          }
        }
        return next;
      });
      if (preservedErrorStamp.current === stampAtStart) {
        setError(null);
      }
    } catch (err) {
      if (preservedErrorStamp.current !== stampAtStart) {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load CK trade-in report');
      setItems([]);
      setTotalCards(0);
      setSummary(null);
      setGeneratedAt(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  const loadOpenBatches = useCallback(async () => {
    try {
      const rows = await listOpenCkTradeinBatches();
      setOpenBatches(rows);
    } catch (err) {
      preservedErrorStamp.current += 1;
      setError(formatApiError(err, 'Failed to load open batches'));
      setOpenBatches([]);
    }
  }, []);

  const rememberPayout = useCallback((data: CkTradeinBatch) => {
    setPayoutAmount(data.actual_paid === null || data.actual_paid === undefined ? '' : String(data.actual_paid));
    setRecordedMode(data.payout_mode === 'credit' ? 'credit' : 'cash');
    if (data.paid_at) {
      const parsed = parseInstant(data.paid_at);
      if (parsed) {
        setPaidDate(localCalendarDateIso(parsed));
      }
    }
  }, []);

  const loadBatch = useCallback(async (id: string) => {
    try {
      setBatchLoading(true);
      setError(null);
      const data = await fetchCkTradeinBatch(id);
      setBatch(data);
      setLineQty(Object.fromEntries(data.lines.map((line) => [line.id, String(line.qty_requested)])));
      setPullQty(Object.fromEntries(data.lines.map((line) => [line.id, defaultActionQty(line)])));
      rememberPayout(data);
    } catch (err) {
      setBatch(null);
      setError(formatApiError(err, 'Failed to load batch'));
    } finally {
      setBatchLoading(false);
    }
  }, [rememberPayout]);

  const loadPickList = useCallback(async (id: string) => {
    try {
      setPickLoading(true);
      setError(null);
      const data = await fetchCkTradeinPickList(id);
      setPickList(data);
    } catch (err) {
      setPickList(null);
      setError(formatApiError(err, 'Failed to load pick list'));
    } finally {
      setPickLoading(false);
    }
  }, []);

  const loadReportRef = useRef(loadReport);
  loadReportRef.current = loadReport;

  // Candidate filters apply only when the form is submitted. Batch routes load the frozen batch.
  useEffect(() => {
    setCkExport(null);
    if (batchId) {
      setBatch(null);
      setWorkflowStep('batch');
      setPickList(null);
      void loadBatch(batchId);
      return;
    }
    void loadReportRef.current();
    void loadOpenBatches();
  }, [batchId, loadBatch, loadOpenBatches]);

  useEffect(() => {
    if (batchId || !error) {
      return;
    }
    candidateErrorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [batchId, error]);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const toggleSelected = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const visibleKeys = items.map((item) => candidateKey(item));
  const selectedVisibleCount = visibleKeys.filter((key) => selectedKeys.has(key)).length;
  const allVisibleSelected = visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  useEffect(() => {
    if (pickAllRef.current) {
      pickAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleVisibleSelection = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const key of visibleKeys) {
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const handleStartBatch = async () => {
    const selected = items.filter((item) => selectedKeys.has(candidateKey(item)));
    if (selected.length === 0) {
      preservedErrorStamp.current += 1;
      setError('Select at least one card');
      return;
    }

    const lines = [];
    for (const item of selected) {
      const key = candidateKey(item);
      const qty = parseInt(qtyByKey[key] ?? String(item.copy_count), 10);
      if (!Number.isInteger(qty) || qty < 1 || qty > item.copy_count) {
        preservedErrorStamp.current += 1;
        setError(`Quantity for ${item.name} must be between 1 and ${item.copy_count}`);
        return;
      }
      lines.push({
        mtgjson_uuid: item.mtgjson_uuid,
        condition: item.condition || 'NM',
        printing: item.printing || (item.is_foil ? 'foil' : 'normal'),
        language: item.language || 'EN',
        qty,
      });
    }

    try {
      setIsStarting(true);
      setError(null);
      const created = await createCkTradeinBatch({ payout_mode: payout, lines });
      navigate(`/ck-tradein/${created.id}`);
    } catch (err) {
      preservedErrorStamp.current += 1;
      setError(formatApiError(err, 'Failed to start batch'));
    } finally {
      setIsStarting(false);
    }
  };

  const applyBatch = (updated: CkTradeinBatch) => {
    setBatch(updated);
    setLineQty(Object.fromEntries(updated.lines.map((row) => [row.id, String(row.qty_requested)])));
    setPullQty(Object.fromEntries(updated.lines.map((row) => [row.id, defaultActionQty(row)])));
  };

  const runPullAction = async (
    line: CkTradeinBatchLine,
    action: (batchId: string, lineId: string, qty: number) => Promise<CkTradeinBatch>,
    fallback: string,
  ) => {
    if (!batch) {
      return;
    }
    const qty = parseInt(pullQty[line.id] ?? defaultActionQty(line), 10);
    if (!Number.isInteger(qty) || qty < 1) {
      setError(`Quantity for ${line.name} must be at least 1`);
      return;
    }
    try {
      setSavingLineId(line.id);
      setError(null);
      applyBatch(await action(batch.id, line.id, qty));
    } catch (err) {
      setError(formatApiError(err, fallback));
    } finally {
      setSavingLineId(null);
    }
  };

  const handleUpdateLine = async (line: CkTradeinBatchLine) => {
    if (!batch) {
      return;
    }
    const qty = parseInt(lineQty[line.id] ?? String(line.qty_requested), 10);
    if (!Number.isInteger(qty) || qty < 1) {
      setError(`Quantity for ${line.name} must be at least 1`);
      return;
    }
    try {
      setSavingLineId(line.id);
      setError(null);
      const updated = await updateCkTradeinLine(batch.id, line.id, qty);
      applyBatch(updated);
    } catch (err) {
      setError(formatApiError(err, 'Failed to update line'));
    } finally {
      setSavingLineId(null);
    }
  };

  const handleRemoveLine = async (line: CkTradeinBatchLine) => {
    if (!batch) {
      return;
    }
    try {
      setSavingLineId(line.id);
      setError(null);
      const updated = await removeCkTradeinLine(batch.id, line.id);
      applyBatch(updated);
    } catch (err) {
      setError(formatApiError(err, 'Failed to remove line'));
    } finally {
      setSavingLineId(null);
    }
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      await downloadCkTradeinCsv(buildParams());
    } catch (err) {
      preservedErrorStamp.current += 1;
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCkCsv = async () => {
    if (!batch || batch.status === 'shipped' || isExportingCk) {
      return;
    }
    try {
      setIsExportingCk(true);
      setError(null);
      const result = await exportCkTradeinCsv(batch.id);
      setCkExport(result);
      if (result.batch) {
        setBatch(result.batch);
        setLineQty(Object.fromEntries(result.batch.lines.map((row) => [row.id, String(row.qty_requested)])));
        setPullQty(Object.fromEntries(result.batch.lines.map((row) => [row.id, defaultActionQty(row)])));
      }
    } catch (err) {
      setError(formatApiError(err, 'Failed to export CK CSV'));
    } finally {
      setIsExportingCk(false);
    }
  };

  const handleReturnToStock = async (line: CkTradeinBatchLine) => {
    if (!batch) {
      return;
    }
    const qty = parseInt(pullQty[line.id] ?? defaultActionQty(line), 10);
    if (!Number.isInteger(qty) || qty < 1) {
      setError(`Quantity for ${line.name} must be at least 1`);
      return;
    }
    const note = (returnNote[line.id] ?? '').trim();
    try {
      setSavingLineId(line.id);
      setError(null);
      const updated = note
        ? await returnCkTradeinLineToStock(batch.id, line.id, qty, note)
        : await returnCkTradeinLineToStock(batch.id, line.id, qty);
      applyBatch(updated);
      setReturnNote((prev) => ({ ...prev, [line.id]: '' }));
    } catch (err) {
      setError(formatApiError(err, 'Failed to return copies to stock'));
    } finally {
      setSavingLineId(null);
    }
  };

  const handleMarkShipped = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!batch || batch.status === 'shipped' || isShipping) {
      return;
    }
    const pulled = batch.lines.reduce((sum, line) => sum + line.qty_pulled, 0);
    if (pulled < 1) {
      setError('Pull at least one card before shipping');
      return;
    }
    try {
      setIsShipping(true);
      setError(null);
      applyBatch(await markCkTradeinBatchShipped(batch.id, instantFromLocalDate(shipDate)));
    } catch (err) {
      setError(formatApiError(err, 'Failed to mark shipped'));
    } finally {
      setIsShipping(false);
    }
  };

  const handleRecordPayout = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!batch || batch.status !== 'shipped' || isSavingPayout) {
      return;
    }
    const amount = Number(payoutAmount);
    if (payoutAmount.trim() === '' || !Number.isFinite(amount) || amount < 0) {
      setError('Amount paid must be zero or more');
      return;
    }
    try {
      setIsSavingPayout(true);
      setError(null);
      const updated = await recordCkTradeinPayout(batch.id, {
        actual_paid: amount,
        paid_at: instantFromLocalDate(paidDate),
        payout_mode: recordedMode,
      });
      applyBatch(updated);
      rememberPayout(updated);
    } catch (err) {
      setError(formatApiError(err, 'Failed to record payout'));
    } finally {
      setIsSavingPayout(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>CK Trade-In</h1>
        <p style={styles.subtitle}>
          Sellable copies where Card Kingdom trade-in pays the same or more than
          Manapool after 5% + 2.9% seller fees. CK cash uses the MTGJSON buylist
          (NM) with condition haircuts. Store credit uses CK&apos;s typical 30% bonus.
        </p>
      </div>

      <section style={styles.card} aria-label="Workflow">
        <h2 style={styles.sectionTitle}>Workflow</h2>
        <ol style={styles.stepList}>
          {WORKFLOW_STEPS.map((step, index) => {
            const onBatch = Boolean(batchId);
            const pickListActive = onBatch && workflowStep === 'pick-list';
            const isCandidatesCurrent = !onBatch && step.id === 'candidates';
            const isMarkPulledCurrent = onBatch && !pickListActive && step.id === 'mark-pulled';
            const isPickListCurrent = pickListActive && step.id === 'pick-list';
            const isCurrent = isCandidatesCurrent || isMarkPulledCurrent || isPickListCurrent;
            const isCandidatesLink = onBatch && step.id === 'candidates';
            const pickListEnabled = onBatch && step.id === 'pick-list' && !isPickListCurrent;
            const markPulledEnabled = onBatch && step.id === 'mark-pulled' && !isMarkPulledCurrent;
            const isExportStep = step.id === 'export-ck-csv';
            const isShipStep = step.id === 'shipped';
            const isPayoutStep = step.id === 'record-payout';
            const exportReady = Boolean(onBatch && batch && batch.status !== 'shipped');
            const exportDisabled = !exportReady || isExportingCk;
            const stepReady = pickListEnabled || markPulledEnabled || (isExportStep && exportReady);
            const shipReady = Boolean(onBatch && batch);
            const payoutReady = Boolean(batch && batch.status === 'shipped');
            return (
              <li
                key={step.id}
                style={{
                  ...styles.stepItem,
                  ...(isCurrent ? styles.stepCurrent : styles.stepDisabled),
                }}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span style={styles.stepNum}>{index + 1}</span>
                {isCurrent ? (
                  <span>{step.label}</span>
                ) : isCandidatesLink ? (
                  <Link to="/ck-tradein" style={styles.batchLink}>{step.label}</Link>
                ) : pickListEnabled ? (
                  <button
                    type="button"
                    style={styles.stepLinkButton}
                    onClick={() => {
                      setWorkflowStep('pick-list');
                      if (batchId) {
                        void loadPickList(batchId);
                      }
                    }}
                  >
                    {step.label}
                  </button>
                ) : markPulledEnabled ? (
                  <button
                    type="button"
                    style={styles.stepLinkButton}
                    onClick={() => setWorkflowStep('batch')}
                  >
                    {step.label}
                  </button>
                ) : isExportStep ? (
                  <button
                    type="button"
                    disabled={exportDisabled}
                    style={exportDisabled ? styles.stepButton : styles.stepLinkButton}
                    onClick={() => {
                      document.getElementById('ck-csv-export')?.scrollIntoView?.({ behavior: 'smooth' });
                      void handleExportCkCsv();
                    }}
                  >
                    {step.label}
                  </button>
                ) : isShipStep ? (
                  <button
                    type="button"
                    disabled={!shipReady}
                    style={shipReady ? styles.stepLinkButton : styles.stepButton}
                    onClick={() => {
                      document.getElementById('ck-shipped')?.scrollIntoView?.({ behavior: 'smooth' });
                    }}
                  >
                    {step.label}
                  </button>
                ) : isPayoutStep ? (
                  <button
                    type="button"
                    disabled={!payoutReady}
                    style={payoutReady ? styles.stepLinkButton : styles.stepButton}
                    onClick={() => {
                      document.getElementById('ck-record-payout')?.scrollIntoView?.({ behavior: 'smooth' });
                    }}
                  >
                    {step.label}
                  </button>
                ) : (
                  <button type="button" disabled style={styles.stepButton}>
                    {step.label}
                  </button>
                )}
                {isCurrent ? (
                  <span style={styles.stepBadge}>Current</span>
                ) : (
                  <span style={styles.stepBadgeMuted}>
                    {isCandidatesLink
                      ? 'Done'
                      : isExportStep && batch?.status === 'shipped'
                        ? 'Frozen'
                        : isShipStep && batch?.status === 'shipped'
                          ? 'Done'
                          : isPayoutStep && batch?.paid_at
                            ? 'Done'
                            : stepReady || (isShipStep && shipReady) || (isPayoutStep && payoutReady)
                              ? 'Ready'
                              : 'Later'}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      {error && batchId && <div style={styles.error} role="alert">{error}</div>}

      {!batchId && (
      <section style={styles.card} aria-label="Open batches">
        <h2 style={styles.sectionTitle}>Open batches</h2>
        {openBatches.length === 0 ? (
          <p style={styles.hint}>No open batches.</p>
        ) : (
          <ul style={styles.batchList}>
            {openBatches.map((openBatch) => (
              <li key={openBatch.id} style={styles.batchItem}>
                <Link to={`/ck-tradein/${openBatch.id}`} style={styles.batchLink}>
                  {openBatch.label}
                </Link>
                <span>
                  {' '}
                  · {openBatch.status} · {openBatch.payout_mode} · {formatCurrency(openBatch.expected_total)} · {formatInstant(openBatch.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      )}

      {batchId && (
        <section style={styles.card} aria-label="Batch">
          <h2 style={styles.sectionTitle}>Batch</h2>
          <p style={styles.batchItem}>{batchId}</p>
          {batchLoading && <p style={styles.hint}>Loading batch...</p>}
          {batch && (
            <>
              <p style={styles.summary}>
                {batch.status} · {batch.payout_mode === 'credit' ? 'CK store credit' : 'CK cash'} · Expected total {formatCurrency(batch.expected_total)}
                {batch.actual_paid !== null ? ` · Actual ${formatCurrency(batch.actual_paid)} · Variance ${formatSignedCurrency(batch.variance)}` : ''}
                {' · '}
                Created {formatInstant(batch.created_at)}
                {batch.shipped_at ? ` · Shipped ${formatInstant(batch.shipped_at)}` : ''}
                {batch.paid_at ? ` · Paid ${formatInstant(batch.paid_at)}` : ''}
              </p>
              {workflowStep === 'batch' && batch.status === 'shipped' && (
                <p style={styles.hint}>Lines are frozen because this batch is shipped.</p>
              )}
              {workflowStep === 'batch' && (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.headerRow}>
                      <th style={styles.th}>Card</th>
                      <th style={styles.th}>Set</th>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>Cond</th>
                      <th style={styles.th}>Finish</th>
                      <th style={styles.th}>Lang</th>
                      <th style={styles.th}>Qty</th>
                      <th style={styles.th}>Pulled</th>
                      <th style={styles.th}>CK part</th>
                      <th style={styles.th}>Unit</th>
                      <th style={styles.th}>Line</th>
                      <th style={styles.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {batch.lines.length === 0 && (
                      <tr>
                        <td colSpan={12} style={styles.emptyCell}>This batch has no lines.</td>
                      </tr>
                    )}
                    {batch.lines.map((line) => {
                      const frozen = batch.status === 'shipped';
                      const busy = savingLineId === line.id;
                      const remaining = line.qty_requested - line.qty_pulled;
                      const actionQty = parseInt(pullQty[line.id] ?? defaultActionQty(line), 10);
                      const qtyOk = Number.isInteger(actionQty) && actionQty >= 1;
                      const canPull = !frozen && !busy && qtyOk && remaining > 0 && actionQty <= remaining;
                      const canUndo = !frozen && !busy && qtyOk && line.qty_pulled > 0 && actionQty <= line.qty_pulled;
                      const canReturn = !busy && qtyOk && line.qty_pulled > 0 && actionQty <= line.qty_pulled;
                      const shortfall = line.shortfall_qty ?? 0;
                      const skipped = ckExport?.skipped.find((row) => row.line_id === line.id);
                      const editionWarning = ckExport?.edition_warnings.find((row) => row.line_id === line.id);
                      const linePart = ckExport?.line_parts.find((row) => row.line_id === line.id);
                      const partNumbers = linePart?.csv_part_numbers?.length
                        ? linePart.csv_part_numbers
                        : line.csv_part_number
                          ? [line.csv_part_number]
                          : [];
                      return (
                        <tr key={line.id} style={styles.row}>
                          <td style={styles.tdStrong}>{line.name}</td>
                          <td style={styles.td}>
                            {line.set_name || '-'}
                            {editionWarning && (
                              <div style={styles.warningCaption}>{editionWarning.message}</div>
                            )}
                          </td>
                          <td style={styles.td}>{line.card_number || '-'}</td>
                          <td style={styles.td}>{line.condition}</td>
                          <td style={styles.td}>{line.finish}</td>
                          <td style={styles.td}>{line.language}</td>
                          <td style={styles.td}>
                            <input
                              aria-label={`Quantity for ${line.name}`}
                              type="number"
                              min={1}
                              value={lineQty[line.id] ?? String(line.qty_requested)}
                              disabled={frozen || busy}
                              onChange={(e) => setLineQty((prev) => ({ ...prev, [line.id]: e.target.value }))}
                              style={styles.qtyInput}
                            />
                          </td>
                          <td style={styles.td}>
                            {line.qty_pulled}
                            {shortfall > 0 && (
                              <div style={styles.shortfall}>Shortfall {shortfall}</div>
                            )}
                            {(line.held_cost ?? 0) > 0 && (
                              <div style={styles.hint}>Held {formatCurrency(line.held_cost)}</div>
                            )}
                          </td>
                          <td style={styles.td}>
                            {skipped ? 'not in CK file' : partNumbers.length ? partNumbers.join(', ') : '—'}
                          </td>
                          <td style={styles.tdMoney}>{formatCurrency(line.expected_unit_price)}</td>
                          <td style={styles.tdMoney}>{formatCurrency(line.line_total)}</td>
                          <td style={styles.td}>
                            <div style={styles.actions}>
                              <input
                                aria-label={`Pull quantity for ${line.name}`}
                                type="number"
                                min={1}
                                value={pullQty[line.id] ?? defaultActionQty(line)}
                                disabled={busy}
                                onChange={(e) => setPullQty((prev) => ({ ...prev, [line.id]: e.target.value }))}
                                style={styles.qtyInput}
                              />
                              <button
                                type="button"
                                style={styles.primaryButton}
                                disabled={!canPull}
                                onClick={() => void runPullAction(line, markCkTradeinLinePulled, 'Failed to mark pulled')}
                              >
                                Mark pulled
                              </button>
                              <button
                                type="button"
                                style={styles.secondaryButton}
                                disabled={!canUndo}
                                onClick={() => void runPullAction(line, undoCkTradeinLinePull, 'Failed to undo pull')}
                              >
                                Undo
                              </button>
                              <input
                                aria-label={`Return note for ${line.name}`}
                                type="text"
                                value={returnNote[line.id] ?? ''}
                                disabled={!canReturn}
                                placeholder="Return note"
                                onChange={(e) => setReturnNote((prev) => ({ ...prev, [line.id]: e.target.value }))}
                                style={styles.noteInput}
                              />
                              <button
                                type="button"
                                style={styles.secondaryButton}
                                disabled={!canReturn}
                                onClick={() => void handleReturnToStock(line)}
                              >
                                Return to stock
                              </button>
                              <button
                                type="button"
                                style={styles.secondaryButton}
                                disabled={frozen || busy}
                                onClick={() => void handleUpdateLine(line)}
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                style={styles.secondaryButton}
                                disabled={frozen || busy || line.qty_pulled > 0}
                                onClick={() => void handleRemoveLine(line)}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </section>
      )}

      {batchId && batch && (
        <section id="ck-csv-export" style={styles.card} aria-label="Export CK CSV">
          <h2 style={styles.sectionTitle}>Export CK CSV</h2>
          <p style={styles.hint}>
            Sell-to-CK import file with columns title, edition, foil, qty. English near mint only.
            Played and non-English lines stay on the batch and are listed as not in the CK file.
            Edition is the SyncBase set name.
          </p>
          {batch.status === 'shipped' ? (
            <p style={styles.hint}>Export is frozen because this batch is shipped.</p>
          ) : (
            <div style={styles.actions}>
              <button
                type="button"
                style={styles.primaryButton}
                disabled={isExportingCk}
                onClick={() => void handleExportCkCsv()}
              >
                {isExportingCk ? 'Exporting...' : 'Export CK CSV'}
              </button>
            </div>
          )}
          {ckExport && (
            <div style={styles.exportResult}>
              {ckExport.qty_label && <p style={styles.warningText}>{ckExport.qty_label}</p>}
              <p style={styles.summary}>
                Exported {formatInstant(ckExport.csv_exported_at)}
                {ckExport.csv_part_count ? ` · ${ckExport.csv_part_count} part${ckExport.csv_part_count === 1 ? '' : 's'}` : ''}
              </p>
              {ckExport.parts.length === 0 ? (
                <p style={styles.hint}>No rows were written to a CK file.</p>
              ) : (
                <ul style={styles.batchList}>
                  {ckExport.parts.map((part) => (
                    <li key={part.part_number} style={styles.batchItem}>
                      <button
                        type="button"
                        style={styles.stepLinkButton}
                        onClick={() => saveCkCsvPart(part.filename, part.csv)}
                      >
                        {part.filename}
                      </button>
                      <span>
                        {' '}
                        · part {part.part_number} · {part.line_count} lines · {part.card_count} cards
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <h3 style={styles.subheading}>Not in CK file</h3>
              {ckExport.skipped.length === 0 ? (
                <p style={styles.hint}>Every line was exported.</p>
              ) : (
                <ul style={styles.batchList}>
                  {ckExport.skipped.map((row) => (
                    <li key={row.line_id} style={styles.batchItem}>
                      {row.name} · {row.reason} · {row.detail}
                    </li>
                  ))}
                </ul>
              )}
              <h3 style={styles.subheading}>Alt-art collisions</h3>
              {ckExport.collisions.length === 0 ? (
                <p style={styles.hint}>No name, edition, and foil collisions.</p>
              ) : (
                <ul style={styles.batchList}>
                  {ckExport.collisions.map((row) => (
                    <li key={`${row.title}|${row.edition}|${row.foil}`} style={styles.batchItem}>
                      {row.title} · {row.edition || 'blank edition'} · foil {row.foil ? 'true' : 'false'} · {row.message}
                    </li>
                  ))}
                </ul>
              )}
              <h3 style={styles.subheading}>Edition warnings</h3>
              {ckExport.edition_warnings.length === 0 ? (
                <p style={styles.hint}>No lines on this batch.</p>
              ) : (
                <ul style={styles.batchList}>
                  {ckExport.edition_warnings.map((row) => (
                    <li key={row.line_id} style={styles.batchItem}>
                      {row.title} · {row.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {batchId && workflowStep === 'pick-list' && (
        <section className="ck-tradein-pick-list" style={styles.card} aria-label="Pick list">
          <div className="ck-tradein-no-print" style={styles.pickToolbar}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => setWorkflowStep('batch')}
            >
              Batch lines
            </button>
            <button
              type="button"
              style={styles.primaryButton}
              onClick={() => window.print()}
              disabled={pickLoading || !pickList}
            >
              Print pick list
            </button>
          </div>
          <h2 style={styles.sectionTitle}>Pick list</h2>
          <p style={styles.hint}>
            Warehouse order: set name, then card name. Bins are the current sellable locations.
            Played and non-English lines stay on this list and are marked not in the CK import file.
          </p>
          {pickLoading && <p style={styles.hint}>Loading pick list...</p>}
          {pickList && (
            <p style={styles.summary}>
              {pickList.lines.length} lines · Bins refreshed {formatInstant(pickList.generated_at)}
            </p>
          )}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Set</th>
                  <th style={styles.th}>Card</th>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Finish</th>
                  <th style={styles.th}>Cond</th>
                  <th style={styles.th}>Lang</th>
                  <th style={styles.th}>Qty</th>
                  <th style={styles.th}>Bins</th>
                  <th style={styles.th}>CK file</th>
                </tr>
              </thead>
              <tbody>
                {!pickLoading && pickList && pickList.lines.length === 0 && (
                  <tr>
                    <td colSpan={9} style={styles.emptyCell}>This batch has no lines.</td>
                  </tr>
                )}
                {pickList?.lines.map((line) => (
                  <tr key={line.id} style={styles.row}>
                    <td style={styles.td}>{line.set_name || '-'}</td>
                    <td style={styles.tdStrong}>{line.name}</td>
                    <td style={styles.td}>{line.card_number || '-'}</td>
                    <td style={styles.td}>{line.finish}</td>
                    <td style={styles.td}>{line.condition}</td>
                    <td style={styles.td}>{line.language}</td>
                    <td style={styles.td}>{line.qty_requested}</td>
                    <td style={styles.td}>{line.locations.length ? line.locations.join(', ') : '-'}</td>
                    <td style={line.ck_file_note ? styles.tdNote : styles.td}>{line.ck_file_note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {batchId && batch && (
        <section id="ck-shipped" style={styles.card} aria-label="Shipped">
          <h2 style={styles.sectionTitle}>Shipped</h2>
          <p style={styles.hint}>
            Shipping freezes line edits, pull undo, and CK CSV export. It does not change inventory.
            Cards Card Kingdom rejects stay on hold until you return them to stock.
          </p>
          {batch.status === 'shipped' && batch.shipped_at ? (
            <p style={styles.summary}>Shipped {formatInstant(batch.shipped_at)}</p>
          ) : (
            <form style={styles.filtersRow} onSubmit={(event) => void handleMarkShipped(event)}>
              <label style={styles.filterLabel}>
                Ship date
                <input
                  aria-label="Ship date"
                  type="date"
                  value={shipDate}
                  onChange={(e) => setShipDate(e.target.value)}
                  style={styles.input}
                />
              </label>
              <button
                type="submit"
                style={styles.primaryButton}
                disabled={isShipping || batch.lines.every((line) => line.qty_pulled < 1)}
              >
                {isShipping ? 'Shipping...' : 'Mark shipped'}
              </button>
              {batch.lines.every((line) => line.qty_pulled < 1) && (
                <p style={styles.hint}>Pull at least one card before shipping.</p>
              )}
            </form>
          )}
        </section>
      )}

      {batchId && batch && (
        <section id="ck-record-payout" style={styles.card} aria-label="Record payout">
          <h2 style={styles.sectionTitle}>Record payout</h2>
          <p style={styles.hint}>
            One total for the batch. It is not split across cards. Cash is the Card Kingdom proceeds
            line. Store credit is recorded here and is not sales revenue.
          </p>
          {batch.status !== 'shipped' ? (
            <p style={styles.hint}>Ship the batch before recording the payout.</p>
          ) : (
            <>
              <p style={styles.summary}>
                Expected {formatCurrency(batch.expected_total)}
                {batch.actual_paid !== null
                  ? ` · Actual ${formatCurrency(batch.actual_paid)} · Variance ${formatSignedCurrency(batch.variance)} · Paid ${formatInstant(batch.paid_at)}`
                  : ''}
              </p>
              <form style={styles.filtersRow} onSubmit={(event) => void handleRecordPayout(event)}>
                <label style={styles.filterLabel}>
                  Amount paid
                  <input
                    aria-label="Amount paid"
                    type="number"
                    min={0}
                    step="0.01"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    style={styles.input}
                  />
                </label>
                <label style={styles.filterLabel}>
                  Paid date
                  <input
                    aria-label="Paid date"
                    type="date"
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                    style={styles.input}
                  />
                </label>
                <label style={styles.filterLabel}>
                  Cash or credit
                  <select
                    aria-label="Cash or credit"
                    value={recordedMode}
                    onChange={(e) => setRecordedMode(e.target.value === 'credit' ? 'credit' : 'cash')}
                    style={styles.input}
                  >
                    <option value="cash">CK cash</option>
                    <option value="credit">CK store credit</option>
                  </select>
                </label>
                <button
                  type="submit"
                  style={styles.primaryButton}
                  disabled={isSavingPayout || payoutAmount.trim() === ''}
                >
                  {isSavingPayout ? 'Saving...' : 'Save payout'}
                </button>
              </form>
            </>
          )}
        </section>
      )}

      {!batchId && (
      <section aria-label="Candidates">
        <h2 style={styles.sectionTitle}>Candidates</h2>
        <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
          <label style={styles.filterLabel}>
            Payout
            <select
              aria-label="Payout"
              value={payout}
              onChange={(e) => setPayout(e.target.value === 'credit' ? 'credit' : 'cash')}
              style={styles.input}
            >
              <option value="cash">CK cash</option>
              <option value="credit">CK store credit (+30%)</option>
            </select>
          </label>

          <label style={styles.filterLabel}>
            Top N
            <input
              aria-label="Top N"
              type="number"
              min={1}
              value={topNInput}
              onChange={(e) => setTopNInput(e.target.value)}
              style={styles.input}
            />
          </label>

          <label style={styles.filterLabel}>
            Min Advantage
            <input
              aria-label="Min Advantage"
              type="number"
              min={0}
              step="0.01"
              value={minAdvantageInput}
              onChange={(e) => setMinAdvantageInput(e.target.value)}
              style={styles.input}
            />
          </label>

          <label style={styles.filterLabel}>
            Min CK Price
            <input
              aria-label="Min CK Price"
              type="number"
              min={0}
              step="0.01"
              value={minCkPriceInput}
              onChange={(e) => setMinCkPriceInput(e.target.value)}
              style={styles.input}
              placeholder="optional"
            />
          </label>

          <div style={styles.actions}>
            <button type="submit" style={styles.primaryButton} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Apply'}
            </button>
            <button type="button" style={styles.secondaryButton} onClick={handleExportCsv} disabled={isExporting || isLoading}>
              {isExporting ? 'Exporting...' : 'Export analysis CSV'}
            </button>
            <button
              type="button"
              style={selectedKeys.size === 0 || isStarting || isLoading ? styles.disabledButton : styles.primaryButton}
              onClick={() => void handleStartBatch()}
              disabled={isStarting || isLoading || selectedKeys.size === 0}
              aria-describedby={selectedKeys.size === 0 ? 'ck-start-batch-hint' : undefined}
            >
              {isStarting ? 'Starting...' : 'Start batch'}
            </button>
            {selectedKeys.size === 0 && (
              <span id="ck-start-batch-hint" style={styles.hint}>Select at least one card</span>
            )}
          </div>
          {error && (
            <div ref={candidateErrorRef} style={styles.candidateError} role="alert">
              {error}
            </div>
          )}
        </form>

        <div style={styles.summary}>
          Showing {items.length} of {totalCards} winning rows
          {summary
            ? ` · ${summary.copy_count} copies · CK ${formatCurrency(summary.ck_payout_total)} vs Manapool net ${formatCurrency(summary.manapool_net_total)} · advantage ${formatCurrency(summary.advantage_total)} · scanned ${summary.scanned_rows} (no CK ${summary.skipped_no_ck_buylist}, no Manapool ${summary.skipped_no_manapool_price})`
            : ''}
          {' · '}
          Generated {formatInstant(generatedAt, '-')}
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.th}>
                  <span style={styles.pickHeader}>
                    <input
                      ref={pickAllRef}
                      type="checkbox"
                      aria-label="Select all cards in this result"
                      checked={allVisibleSelected}
                      disabled={items.length === 0}
                      onChange={(event) => toggleVisibleSelection(event.target.checked)}
                    />
                    Pick
                  </span>
                </th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Card</th>
                <th style={styles.th}>Set</th>
                <th style={styles.th}>Cond</th>
                <th style={styles.th}>Finish</th>
                <th style={styles.th}>Lang</th>
                <th style={styles.th}>Copies</th>
                <th style={styles.th}>CK Cash</th>
                <th style={styles.th}>CK Credit</th>
                <th style={styles.th}>Manapool Net</th>
                <th style={styles.th}>Advantage</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Locations</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={15} style={styles.emptyCell}>No cards beat Manapool net with the selected payout.</td>
                </tr>
              )}
              {items.map((item) => {
                const key = candidateKey(item);
                return (
                <tr key={key} style={styles.row}>
                  <td style={styles.td}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.name}`}
                      checked={selectedKeys.has(key)}
                      onChange={(e) => toggleSelected(key, e.target.checked)}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      aria-label={`Quantity for ${item.name}`}
                      type="number"
                      min={1}
                      max={item.copy_count}
                      value={qtyByKey[key] ?? String(item.copy_count)}
                      onChange={(e) => setQtyByKey((prev) => ({ ...prev, [key]: e.target.value }))}
                      style={styles.qtyInput}
                    />
                  </td>
                  <td style={styles.td}>{item.rank}</td>
                  <td style={styles.tdStrong}>{item.name}</td>
                  <td style={styles.td}>{item.set_name || '-'}</td>
                  <td style={styles.td}>{item.condition || '-'}</td>
                  <td style={styles.td}>{item.printing || (item.is_foil ? 'foil' : 'normal')}</td>
                  <td style={styles.td}>{item.language || '-'}</td>
                  <td style={styles.td}>{item.copy_count}</td>
                  <td style={styles.tdMoney}>{formatCurrency(item.ck_cash)}</td>
                  <td style={styles.td}>{formatCurrency(item.ck_credit)}</td>
                  <td style={styles.td}>{formatCurrency(item.manapool_net)}</td>
                  <td style={styles.tdMoney}>{formatCurrency(item.advantage)}</td>
                  <td style={styles.td}>{formatPriceSource(item.manapool_price_source)}</td>
                  <td style={styles.td}>{item.locations?.length ? item.locations.join(', ') : '-'}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      )}
    </div>
  );
};

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '-';
  }
  return `$${value.toFixed(2)}`;
}

function formatSignedCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  const abs = Math.abs(value).toFixed(2);
  return value < 0 ? `-$${abs}` : `$${abs}`;
}

function formatPriceSource(source: string | null): string {
  if (source === 'listing') {
    return 'Manapool listing − fees';
  }
  if (source === 'sell_base') {
    return 'Sell-base (already net)';
  }
  return source || '-';
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
  },
  header: {
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: spacing.sm,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  card: {
    marginBottom: spacing.lg,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  hint: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  batchList: {
    margin: `0 0 ${spacing.xs}`,
    paddingLeft: spacing.lg,
  },
  batchItem: {
    fontSize: typography.body.fontSize,
    color: colors.text,
  },
  batchLink: {
    color: colors.primary,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  noteInput: {
    width: 160,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  qtyInput: {
    width: 72,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  stepList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
  },
  stepCurrent: {
    backgroundColor: colors.gray50,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  stepDisabled: {
    color: colors.textSecondary,
  },
  stepNum: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.gray100,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  stepButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  stepLinkButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    color: colors.primary,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
  },
  subheading: {
    marginTop: spacing.md,
    marginBottom: spacing.xs,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  exportResult: {
    marginTop: spacing.md,
  },
  warningText: {
    margin: `0 0 ${spacing.sm}`,
    color: colors.warning,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  warningCaption: {
    marginTop: spacing.xs,
    color: colors.warning,
    fontSize: typography.caption.fontSize,
  },
  pickToolbar: {
    display: 'flex',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  tdNote: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.warning,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  stepBadge: {
    marginLeft: 'auto',
    fontSize: typography.caption.fontSize,
    color: colors.primary,
  },
  stepBadgeMuted: {
    marginLeft: 'auto',
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    marginBottom: spacing.md,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '160px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
    alignItems: 'center',
  },
  shortfall: {
    color: colors.error,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  disabledButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'not-allowed',
    opacity: 0.45,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  candidateError: {
    flex: '1 1 100%',
    marginBottom: 0,
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
  pickHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
  summary: {
    marginBottom: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  tableWrap: {
    overflowX: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1280px',
  },
  headerRow: {
    backgroundColor: colors.gray100,
    borderBottom: `1px solid ${colors.border}`,
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  row: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  tdMoney: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.success,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
};
